import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  HStack,
  VStack,
  Image,
  Text,
  Button,
  IconButton,
  Tooltip,
  Progress,
  Badge,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiImage, FiX } from 'react-icons/fi';
import { firebase } from '../../../utils/init-firebase';
import { useAuth } from '../../../contexts/AuthContext';
import { compressImageForMms } from '../../../utils/compressImage';

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];

function formatFileSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Single-image picker for SMS -> MMS.
 *
 * Compresses the chosen image under Dialpad's 500 KiB limit, uploads it to
 * `sms-media/{uid}/...` in Firebase Storage, and reports the stored object back
 * via onChange as `{ url, name, size, mimeType }` (or null when removed).
 *
 * @param {Object} props
 * @param {{url:string,name:string,size:number,mimeType:string}|null} props.media
 * @param {(media: object|null) => void} props.onChange
 * @param {boolean} [props.isDisabled]
 * @param {string}  [props.label]
 */
export default function SmsMediaPicker({ media, onChange, isDisabled = false, label = 'Add Image (MMS)' }) {
  const fileInputRef = useRef(null);
  const mountedRef = useRef(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const { currentUser } = useAuth();
  const toast = useToast();
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const previewBg = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const handlePick = () => fileInputRef.current?.click();

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast({
        title: 'Unsupported image type',
        description: 'Please choose a PNG, JPEG, GIF, or WebP image.',
        status: 'warning',
        duration: 3500,
      });
      return;
    }

    setUploading(true);
    setProgress(0);
    try {
      const prepared = await compressImageForMms(file);

      const uid = currentUser?.uid || 'anonymous';
      const path = `sms-media/${uid}/${Date.now()}_${prepared.name}`;
      const storageRef = firebase.storage().ref(path);
      const uploadTask = storageRef.put(prepared);

      await new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snap) => {
            if (mountedRef.current) {
              setProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100));
            }
          },
          reject,
          resolve
        );
      });

      const url = await storageRef.getDownloadURL();
      if (mountedRef.current) {
        onChange({ url, name: prepared.name, size: prepared.size, mimeType: prepared.type });
      }
    } catch (err) {
      console.error('SMS media upload failed:', err);
      if (mountedRef.current) {
        toast({
          title: 'Could not add image',
          description: err.message || 'Check the console for details.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      if (mountedRef.current) {
        setUploading(false);
        setProgress(0);
      }
    }
  };

  return (
    <Box>
      <input
        type="file"
        ref={fileInputRef}
        accept={ALLOWED_TYPES.join(',')}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {!media ? (
        <Button
          size="xs"
          variant="outline"
          leftIcon={<FiImage />}
          onClick={handlePick}
          isLoading={uploading}
          loadingText="Adding image..."
          isDisabled={isDisabled}
        >
          {label}
        </Button>
      ) : (
        <HStack
          spacing={3}
          p={2}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="md"
          bg={previewBg}
          align="center"
        >
          <Image
            src={media.url}
            alt={media.name || 'attachment'}
            boxSize="48px"
            objectFit="cover"
            borderRadius="md"
            fallbackSrc="https://via.placeholder.com/48?text=IMG"
          />
          <VStack spacing={0} align="start" flex={1} minW={0}>
            <HStack spacing={2}>
              <Badge colorScheme="purple">MMS</Badge>
              <Text fontSize="xs" noOfLines={1}>{media.name}</Text>
            </HStack>
            <Text fontSize="xs" color="gray.500">{formatFileSize(media.size)}</Text>
          </VStack>
          <Tooltip label="Remove image">
            <IconButton
              icon={<FiX />}
              aria-label="Remove image"
              size="xs"
              variant="ghost"
              isDisabled={isDisabled || uploading}
              onClick={() => onChange(null)}
            />
          </Tooltip>
        </HStack>
      )}

      {uploading && (
        <Progress size="xs" value={progress} colorScheme="purple" borderRadius="full" mt={2} />
      )}
    </Box>
  );
}
